import type { PickupMethodKey, PickupMethods } from "@/mocks/branch/settings";
import ToggleSwitch from "@/pages/branch/menu/components/ToggleSwitch";
import { PICKUP_METHOD_OPTIONS } from "../constants";

interface PickupPolicySectionProps {
  methods: PickupMethods;
  estimatedMinutes: number;
  payOnPickup: boolean;
  /** حالة خدمة التوصيل بواسطة المحل — للعرض فقط (تُدار من لوحة المحل التجاري) */
  deliveryEnabled: boolean;
  /** رسوم التوصيل الحالية — للعرض فقط */
  deliveryFee: number;
  onToggleMethod: (key: PickupMethodKey, value: boolean) => void;
  onChangeEstimated: (minutes: number) => void;
  onTogglePayOnPickup: (value: boolean) => void;
}

export default function PickupPolicySection({
  methods,
  estimatedMinutes,
  payOnPickup,
  deliveryEnabled,
  deliveryFee,
  onToggleMethod,
  onChangeEstimated,
  onTogglePayOnPickup,
}: PickupPolicySectionProps) {
  const step = (delta: number) => {
    onChangeEstimated(Math.max(5, Math.min(120, estimatedMinutes + delta)));
  };

  return (
    <section className="bg-background-50 rounded-card border border-background-200 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200">
        <div className="w-9 h-9 rounded-btn bg-accent-100 flex items-center justify-center text-accent-600">
          <i className="ri-handbag-line text-xl"></i>
        </div>
        <div>
          <h2 className="text-base font-bold text-foreground-900">سياسة الاستلام</h2>
          <p className="text-xs text-foreground-500 mt-0.5">
            طرق الاستلام المتاحة للعملاء والوقت التقديري للتجهيز
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PICKUP_METHOD_OPTIONS.map((option) => {
            const enabled = methods[option.key];
            return (
              <div
                key={option.key}
                className={`rounded-btn border p-4 transition-colors ${
                  enabled
                    ? "border-primary-200 bg-primary-50"
                    : "border-background-200 bg-background-100"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-btn flex items-center justify-center bg-background-50 border border-background-200">
                    <i
                      className={`${option.icon} text-xl ${
                        enabled ? "text-primary-600" : "text-foreground-400"
                      }`}
                    ></i>
                  </div>
                  <ToggleSwitch
                    checked={enabled}
                    onChange={() => onToggleMethod(option.key, !enabled)}
                    label={`تفعيل ${option.label}`}
                  />
                </div>
                <p
                  className={`mt-3 text-sm font-bold ${
                    enabled ? "text-foreground-900" : "text-foreground-400"
                  }`}
                >
                  {option.label}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
                  {option.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3.5 rounded-btn bg-secondary-50 border border-secondary-200">
          <div>
            <p className="text-sm font-bold text-foreground-900">
              الوقت التقديري لتجهيز الطلب
            </p>
            <p className="text-xs text-foreground-500 mt-0.5">
              يظهر للعميل أثناء إتمام الطلب
            </p>
          </div>

          <div className="flex items-center border border-background-300 rounded-btn overflow-hidden bg-background-50">
            <button
              type="button"
              onClick={() => step(5)}
              aria-label="زيادة الوقت التقديري"
              className="w-10 h-11 flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-add-line text-lg"></i>
            </button>
            <div className="h-11 px-3 flex items-center gap-1 border-x border-background-200">
              <span className="text-base font-bold text-foreground-900 whitespace-nowrap">
                {estimatedMinutes}
              </span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">دقيقة</span>
            </div>
            <button
              type="button"
              onClick={() => step(-5)}
              aria-label="تقليل الوقت التقديري"
              className="w-10 h-11 flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line text-lg"></i>
            </button>
          </div>
        </div>

        <div
          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3.5 rounded-btn border transition-colors ${
            payOnPickup
              ? "bg-secondary-50 border-secondary-200"
              : "bg-background-100 border-background-200"
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-9 h-9 rounded-btn flex items-center justify-center border shrink-0 ${
                payOnPickup
                  ? "bg-background-50 border-secondary-200 text-secondary-600"
                  : "bg-background-50 border-background-200 text-foreground-400"
              }`}
            >
              <i className="ri-cash-line text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground-900">
                الدفع عند الاستلام
              </p>
              <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">
                {payOnPickup
                  ? "يمكن للعميل الدفع نقدًا وقت استلام الطلب"
                  : "معطّل — لن يظهر خيار الدفع عند الاستلام للعميل"}
              </p>
            </div>
          </div>

          <ToggleSwitch
            checked={payOnPickup}
            onChange={() => onTogglePayOnPickup(!payOnPickup)}
            label="تفعيل الدفع عند الاستلام"
          />
        </div>

        {/* التوصيل بواسطة المحل — للعرض فقط، بدون سويتش أو حقل تعديل */}
        <div className="rounded-btn border border-background-200 bg-background-100 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-btn bg-background-50 border border-background-200 flex items-center justify-center shrink-0">
                <i className="ri-e-bike-2-line text-xl text-foreground-500"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-900">التوصيل بواسطة المحل</p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  خدمة توصيل الطلبات من الفرع إلى العميل
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-bold whitespace-nowrap shrink-0">
              <i className="ri-lock-line"></i>
              للعرض فقط
            </span>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-btn bg-background-50 border border-background-200 p-3">
              <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                <i className="ri-toggle-line"></i>
                حالة الخدمة
              </p>
              <p
                className={`mt-1.5 inline-flex items-center gap-1.5 text-sm font-bold ${
                  deliveryEnabled ? "text-primary-700" : "text-foreground-400"
                }`}
              >
                <i
                  className={
                    deliveryEnabled ? "ri-checkbox-circle-fill" : "ri-close-circle-line"
                  }
                ></i>
                {deliveryEnabled ? "مفعّل" : "غير مفعّل"}
              </p>
            </div>
            <div className="rounded-btn bg-background-50 border border-background-200 p-3">
              <p className="text-xs text-foreground-500 flex items-center gap-1.5">
                <i className="ri-money-cny-circle-line"></i>
                رسوم التوصيل الحالية
              </p>
              <p className="mt-1.5 text-sm font-bold text-foreground-900">
                {deliveryFee.toFixed(2)} ر.س
              </p>
            </div>
          </div>

          <p className="mt-3 flex items-start gap-1.5 text-xs text-foreground-500 leading-relaxed">
            <i className="ri-information-line text-base text-foreground-400 shrink-0"></i>
            يتم تحديد حالة التوصيل ورسومه من لوحة المحل التجاري.
          </p>

          {!deliveryEnabled ? (
            <div className="mt-3 flex items-center gap-2 p-3 rounded-btn bg-accent-50 border border-accent-200 text-xs font-semibold text-accent-800">
              <i className="ri-error-warning-line text-base shrink-0"></i>
              التوصيل غير مفعّل لهذا الفرع.
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}